import { useState } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import { AppBar, Toolbar } from '@mui/material'
import AppDrawer from 'components/navigation/AppDrawer'
import ContentContainer from 'components/layout/ContentContainer'
import { selectAuth } from 'store/authentication/slice'
import { selectRoute } from 'store/route/slice'
import LanguageSwitcher from 'components/locale/LanguageSwitcher'
import OverlayLoader from 'components/ui/OverlayLoader'
import Breadcrumbs from 'components/ui/Breadcrumbs'

interface Props {
  children: JSX.Element
  open?: boolean
}

export default function AppContainer({ children }: Props): JSX.Element {
  const [open, setOpen] = useState<boolean>(true)
  const auth = useSelector(selectAuth)
  const routeInfo = useSelector(selectRoute)
  const isAdminRoute = routeInfo?.path?.includes('/admin')
  const toggleDrawerVisibility = () => {
    setOpen(!open)
  }

  return (
    <RootContainer>
      <OverlayLoader visible={auth.status === 'loading'} />
      <StyledAppBar position="fixed" open={open}>
        <NavBar style={{ paddingLeft: 30 }}>
          <PageHeaderContainer>
            {isAdminRoute && (
              <PageHeaderTitle>{routeInfo?.titleId && <FormattedMessage id={routeInfo.titleId} />}</PageHeaderTitle>
            )}
            {!isAdminRoute && <Breadcrumbs route={routeInfo.path} excludePaths={['search']} />}
            {isAdminRoute && <LanguageSwitcher />}
          </PageHeaderContainer>
        </NavBar>
      </StyledAppBar>
      <AppDrawer open={open} toggle={toggleDrawerVisibility} />
      <ContentContainer open={open}>{children}</ContentContainer>
    </RootContainer>
  )
}

const RootContainer = styled.div`
  display: flex;
  min-height: calc(100vh - 80px);

  & main {
    margin-top: 85px;
    margin-left: 250px;
  }
`

const NavBar = styled(Toolbar)`
  display: flex;
  justify-content: space-between;
`

const PageHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const PageHeaderTitle = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 30px;
`

const StyledAppBar = styled(AppBar)<Props>`
  background-color: ${(props) => props.theme.customPalette.light};
  color: ${(props) => props.theme.customPalette.navText};
  box-shadow: none;
  border-bottom: 1px solid ${(props) => props.theme.customPalette.lightGray2};
  width: ${(props) => (props.open ? 'calc(100% - 212px)' : 'calc(100% - 80px)')};
  height: 80px;
  justify-content: center;
`
